import { useMemo } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AnalyticsPage from '../analytics/AnalyticsPage';
import DashboardShell from '../layout/DashboardShell';
import InventoryPage from '../inventory/InventoryPage';
import InvoicePrintPage from '../invoices/InvoicePrintPage';
import InvoicesPage from '../invoices/InvoicesPage';
import OrderDetailPage from '../orders/OrderDetailPage';
import OrdersPage from '../orders/OrdersPage';
import UnshippedOrdersPage from '../orders/UnshippedOrdersPage';
import ProcurementPage from '../procurement/ProcurementPage';
import FulfillmentPage from '../fulfillment/FulfillmentPage';
import ProductsPage from '../products/ProductsPage';
import RevenueCalculatorPage from '../revenue-calculator/RevenueCalculatorPage';
import AssignUsersPage from '../settings/AssignUsersPage';
import AdminHomePage from './AdminHomePage';
import SearchPage from '../search/SearchPage';
import UploadTrackingPage from '../upload-tracking/UploadTrackingPage';
import CustomerServicePage from '../customer-service/CustomerServicePage';
import ReportsPage from '../reports/ReportsPage';
import PurchaseShippingReportPage from '../reports/PurchaseShippingReportPage';
import BatchReportPage from '../reports/BatchReportPage';
import WorkInProgressPage from '../debug/WorkInProgressPage';
import { getBlockedPageLabel, isAdminPathBlocked } from '../debug/blockedPages';
import { adminNavItems, getAdminNavMeta } from './adminNav';
import ModuleAccessDenied from './ModuleAccessDenied';
import NoAccessPage from './NoAccessPage';
import {
  canAccessAdminPath,
  filterNavItemsByModules,
  getDefaultAdminPath,
  getUserModules,
  isWorkspaceUser,
  resolvePathAccess,
} from './moduleAccess';

const pageCopy = {
  '/admin/dashboard': {
    title: 'Workspace home',
    subtitle: 'Account summary and quick links across seller operations.',
  },
  '/admin/search': {
    title: 'Search',
    subtitle: 'Find a HAWB, order, tracking ID, or batch and open the full procurement batch.',
  },
  '/admin/products': {
    title: 'Products',
    subtitle: 'Your Amazon seller listings with ASIN, images, and product links.',
  },
  '/admin/upload-tracking': {
    title: 'Upload Tracking ID',
    subtitle:
      'Paste Amazon Business tracking onto procurement lines by US Order ID and ASIN.',
  },
  '/admin/analytics': {
    title: 'Analytics',
    subtitle: 'Operational KPIs from orders, procurement, and inventory.',
  },
  '/admin/revenue-calculator': {
    title: 'FBM revenue calculator',
    subtitle: 'Estimate Amazon fees and net proceeds for merchant-fulfilled orders.',
  },
  '/admin/orders': {
    title: 'Orders',
    subtitle: 'Amazon India order sync, tracking, and order history.',
  },
  '/admin/unshipped-orders': {
    title: 'Unshipped orders',
    subtitle: 'Open orders from Amazon waiting to ship.',
  },
  '/admin/procurement': {
    title: 'Procurement',
    subtitle: 'Purchase batches linked to order lines and inventory.',
  },
  '/admin/fulfillment': {
    title: 'Fulfillment',
    subtitle:
      'Receive a HAWB, book ITL packets, paste I2I, then work Amazon / Inventory / Not feasible.',
  },
  '/admin/inventory': {
    title: 'Inventory',
    subtitle: 'Stock levels, supplier links, and reorder thresholds.',
  },
  '/admin/invoices': {
    title: 'Invoices',
    subtitle: 'Invoice records generated from synced orders.',
  },
  '/admin/customer-service': {
    title: 'Customer Service',
    subtitle: 'Review delivered orders, prioritize follow-ups, and manage customer confirmations.',
  },
  '/admin/reports': {
    title: 'Reports',
    subtitle: 'Operational reports across orders, procurement, inventory, and analytics.',
  },
  '/admin/reports/purchase-shipping': {
    title: 'Purchase Shipping report',
    subtitle: 'MAR Express Excel format filtered by IN Amazon order date and status.',
  },
  '/admin/reports/batch': {
    title: 'Batch report',
    subtitle: 'Procurement batch lines with buy totals, tracking, HAWB, and Excel download.',
  },
  '/admin/assign-users': {
    title: 'Assign users',
    subtitle: 'Create staff logins and assign limited section access.',
  },
  '/admin/no-access': {
    title: 'No access',
    subtitle: 'Your account needs section permissions from an admin.',
  },
};

function resolvePageCopy(pathname) {
  if (pathname.startsWith('/admin/assign-users/')) {
    if (pathname.endsWith('/edit')) {
      return {
        title: 'Edit user',
        subtitle: 'Update login details and section access.',
      };
    }
    if (pathname.endsWith('/new') || pathname.includes('/new')) {
      return {
        title: 'Create user',
        subtitle: 'Add a staff login and choose which sections they can open.',
      };
    }
    return {
      title: 'Assign users',
      subtitle: 'Create staff logins and assign limited section access.',
    };
  }

  if (pathname.startsWith('/admin/unshipped-orders/')) {
    return {
      title: 'Unshipped order',
      subtitle: 'Open order details, packaging documents, and shipment actions.',
    };
  }

  if (pathname.startsWith('/admin/orders/')) {
    return { title: 'Order detail', subtitle: 'Buyer, shipping, tracking, and line items.' };
  }

  if (pathname.startsWith('/admin/invoices/')) {
    return { title: 'Invoice', subtitle: 'Printable invoice preview.' };
  }

  const exact = pageCopy[pathname];
  if (exact) {
    return exact;
  }

  const nav = getAdminNavMeta(pathname);
  return {
    title: nav.label,
    subtitle: 'Seller workspace',
  };
}

function AdminApp({ user, token, onSignOut, onUserUpdate, blockedPages = [] }) {
  const location = useLocation();
  const copy = resolvePageCopy(location.pathname);
  const fullPath = location.pathname.startsWith('/admin')
    ? location.pathname
    : `/admin${location.pathname === '/' ? '/dashboard' : location.pathname}`;
  const pageBlocked = isAdminPathBlocked(fullPath, blockedPages);
  const defaultPath = getDefaultAdminPath(user);
  const navItems = useMemo(() => filterNavItemsByModules(adminNavItems, user), [user]);
  const modules = useMemo(() => getUserModules(user), [user]);
  const isStaff = isWorkspaceUser(user);
  const isNoAccessRoute = fullPath === '/admin/no-access';
  const hasModuleAccess =
    isNoAccessRoute || pageBlocked || canAccessAdminPath(user, fullPath);
  const deniedRule = resolvePathAccess(fullPath);
  const deniedLabel =
    adminNavItems.find((item) => item.id === deniedRule.module)?.label ||
    (deniedRule.ownerOnly ? 'Assign users' : null);

  return (
    <DashboardShell
      user={user}
      navItems={navItems}
      onSignOut={onSignOut}
      title={
        pageBlocked
          ? 'Work under progress'
          : !hasModuleAccess
            ? 'Access denied'
            : copy.title
      }
      subtitle={
        pageBlocked
          ? 'This section is temporarily unavailable.'
          : !hasModuleAccess
            ? 'This section is not included in your assigned access.'
            : copy.subtitle
      }
      settingsProps={{ user, token, onUserUpdate }}
      notificationToken={token}
    >
      {pageBlocked ? (
        <WorkInProgressPage pageLabel={getBlockedPageLabel(fullPath)} />
      ) : !hasModuleAccess ? (
        isStaff && modules.length === 0 ? (
          <Navigate to="/admin/no-access" replace />
        ) : (
          <ModuleAccessDenied moduleLabel={deniedLabel} />
        )
      ) : (
        <Routes>
          <Route path="/" element={<Navigate to={defaultPath} replace />} />
          <Route path="/dashboard" element={<AdminHomePage user={user} token={token} />} />
          <Route path="/search" element={<SearchPage token={token} />} />
          <Route path="/products" element={<ProductsPage token={token} user={user} />} />
          <Route path="/upload-tracking" element={<UploadTrackingPage token={token} />} />
          <Route path="/asin-tracker" element={<Navigate to="/admin/upload-tracking" replace />} />
          <Route
            path="/revenue-calculator"
            element={<RevenueCalculatorPage token={token} user={user} />}
          />
          <Route path="/analytics" element={<AnalyticsPage token={token} />} />
          <Route path="/orders" element={<OrdersPage token={token} user={user} />} />
          <Route path="/orders/:id" element={<OrderDetailPage token={token} user={user} />} />
          <Route
            path="/unshipped-orders"
            element={<UnshippedOrdersPage token={token} user={user} />}
          />
          <Route
            path="/unshipped-orders/:id"
            element={<OrderDetailPage token={token} user={user} />}
          />
          <Route path="/procurement" element={<ProcurementPage token={token} />} />
          <Route path="/fulfillment" element={<FulfillmentPage token={token} />} />
          <Route path="/inventory" element={<InventoryPage token={token} />} />
          <Route path="/invoices" element={<InvoicesPage token={token} />} />
          <Route path="/invoices/:id" element={<InvoicePrintPage token={token} />} />
          <Route path="/customer-service" element={<CustomerServicePage token={token} />} />
          <Route path="/reports" element={<ReportsPage token={token} />} />
          <Route
            path="/reports/purchase-shipping"
            element={<PurchaseShippingReportPage token={token} />}
          />
          <Route path="/reports/batch" element={<BatchReportPage token={token} />} />
          <Route path="/no-access" element={<NoAccessPage user={user} />} />
          <Route
            path="/assign-users"
            element={<AssignUsersPage token={token} user={user} mode="list" />}
          />
          <Route
            path="/assign-users/new"
            element={<AssignUsersPage token={token} user={user} mode="create" />}
          />
          <Route
            path="/assign-users/:userId/edit"
            element={<AssignUsersPage token={token} user={user} mode="edit" />}
          />
          <Route path="/settings" element={<Navigate to={defaultPath} replace />} />
          <Route path="*" element={<Navigate to={defaultPath} replace />} />
        </Routes>
      )}
    </DashboardShell>
  );
}

export default AdminApp;
